import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { catchError, interval, of, startWith, switchMap } from 'rxjs';

import {
  NuevasPostulacionesResponse,
  PostulacionNueva,
  PostulacionesAlertasService,
} from './postulaciones-alertas.service';

/**
 * Badge del toolbar con las postulaciones nuevas del portal público
 * (Fase A item #2 — notificación al Analista GH).
 *
 * Mismo patrón que AlertasTareasComponent: polling con startWith(0),
 * mat-icon-button + matBadge + dropdown matMenu. Polling a 60s — más
 * agresivo que tareas porque afecta el SLA de contacto al candidato.
 */
@Component({
  selector: 'inv-alertas-postulaciones',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    DatePipe,
    RouterModule,
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  styles: [
    `
      :host { display: inline-flex; }

      /* Igual que en alertas-tareas: sin esto el max-width: 280px del panel de
         mat-menu recorta los 360px del contenido. */
      ::ng-deep .postulaciones-panel-wrapper.mat-mdc-menu-panel {
        max-width: none;
      }

      .postulaciones-panel {
        width: 360px;
        max-width: calc(100vw - 2rem);
        max-height: 440px;
        overflow-y: auto;
      }
    `,
  ],
  template: `
    <button
      mat-icon-button
      [matMenuTriggerFor]="menu"
      [matTooltip]="
        (data?.total ?? 0) > 0
          ? data?.total + ' candidato(s) nuevo(s) del portal público'
          : 'Sin postulaciones nuevas'
      "
      class="!text-default"
    >
      <mat-icon
        [matBadge]="(data?.total ?? 0) > 0 ? data?.total : null"
        matBadgeColor="warn"
        matBadgeSize="small"
      >person_add</mat-icon>
    </button>

    <mat-menu #menu="matMenu" class="postulaciones-panel-wrapper" xPosition="before">
      <div class="postulaciones-panel" (click)="$event.stopPropagation()">
        <div class="px-4 pt-3 pb-2 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div class="flex items-center justify-between">
            <div class="font-semibold text-gray-900 flex items-center gap-2">
              <mat-icon class="!text-base !w-5 !h-5">person_search</mat-icon>
              Postulaciones nuevas
              <!-- Selección es de Invering: decirlo evita que se lea como un aviso
                   del área comercial de Infinitus. -->
              <span class="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-300">
                Invering
              </span>
            </div>
            <button
              mat-icon-button
              (click)="cargar(); $event.stopPropagation()"
              matTooltip="Recargar"
              class="!w-8 !h-8"
            >
              <mat-icon class="!text-base !w-5 !h-5">refresh</mat-icon>
            </button>
          </div>
          <div *ngIf="data" class="text-xs text-secondary mt-1">
            {{ data.total }} sin revisar · llegaron por el chat público
          </div>

          <!-- El aviso existía en las campanas de tareas y de leads, pero acá la
               bandera "desconectado" se calculaba y se tiraba: no había ningún
               ngIf que la pintara. O sea el mismo defecto que este bloque viene
               a arreglar —el sistema sabe que el dato está viejo y no lo dice—,
               y en la campana que más importa: sondea cada 60s porque de eso
               depende el SLA de primer contacto al candidato. -->
          <div
            *ngIf="desconectado"
            class="mt-2 text-xs rounded bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1">
            Sin conexión con el servidor: esto puede estar desactualizado.
          </div>
        </div>

        <button
          *ngFor="let p of data?.items"
          mat-menu-item
          class="!h-auto !py-3"
          (click)="abrir(p)"
        >
          <div class="flex flex-col gap-0.5 w-full">
            <div class="flex items-start gap-2">
              <mat-icon class="text-primary-600 !text-base !w-5 !h-5 flex-shrink-0">
                person_add
              </mat-icon>
              <span class="text-sm font-medium whitespace-normal leading-tight">
                {{ p.candidatoNombres }}
              </span>
            </div>
            <div class="text-[11px] text-secondary pl-7">
              {{ p.requisicionCodigo || 'REQ-' + p.requisicionId }}
              <span *ngIf="p.cargoNombre"> · {{ p.cargoNombre }}</span>
              · {{ p.createdAt | date: 'short' }}
            </div>
          </div>
        </button>

        <!-- Empty state -->
        <div
          *ngIf="data && data.total === 0"
          class="text-center py-10 text-secondary"
        >
          <mat-icon class="!text-5xl !w-12 !h-12 text-gray-300 mb-2">inbox</mat-icon>
          <p class="text-sm">Sin postulaciones nuevas por revisar.</p>
        </div>

        <!-- Footer -->
        <div
          *ngIf="data && data.total > 0"
          class="px-4 py-3 border-t border-gray-200 sticky bottom-0 bg-white"
        >
          <a mat-stroked-button class="!w-full" routerLink="/apps/gh/seleccion">
            Ir al módulo de Selección
          </a>
        </div>
      </div>
    </mat-menu>
  `,
})
export class AlertasPostulacionesComponent implements OnInit {
  private readonly service = inject(PostulacionesAlertasService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  data: NuevasPostulacionesResponse | null = null;

  /** true si el último sondeo falló. Lo que se ve puede estar viejo. */
  desconectado = false;

  ngOnInit(): void {
    // Polling cada 60s — afecta el SLA de primer contacto al candidato.
    interval(60_000)
      .pipe(
        startWith(0),
        // `catchError` va DENTRO del switchMap: si el error sube al stream
        // externo, RxJS lo termina y el interval queda muerto para siempre.
        switchMap(() => this.service.nuevas().pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (d) => {
          if (d === null) {
            this.desconectado = true;
            return;
          }
          this.desconectado = false;
          this.data = d;
        },
      });
  }

  cargar(): void {
    this.service.nuevas().subscribe({
      next: (d) => (this.data = d),
      error: () => undefined,
    });
  }

  /** Abre el kanban de la requisición y marca la postulación como revisada. */
  abrir(p: PostulacionNueva): void {
    this.service.marcarRevisada(p.id).subscribe({
      next: () => this.cargar(),
      error: () => undefined,
    });
    this.router.navigate(['/apps/gh/seleccion', p.requisicionId, 'kanban'], {
      queryParams: { nuevaPostulacionId: p.id },
    });
  }
}
